import type { Geometry, MonitoringSite, ToGeoJSON } from '../../types/apiModels'
import { assignSamplingAreasToMonitoringPoints } from '../../utils/FarmLayers/monitoringSites'
import addMonitoringSiteV2 from '../ruutsApi/v2/monitoring/sites/add'
import { createExclusionAreasFeatureByFarm } from './exclusionAreas/saveExclusionAreas'
import { createOtherPolygon } from './otherPolygons/createOtherPolygon'
import { createOtherSite } from './otherSites/createOtherSite'
import { updatePerimeter } from './perimeter/updatePerimeter'
import { saveFarmSubdivision } from './saveFarmSubdivision'
import { paddocks as paddocksService } from './savePaddocks'
import { createSamplingAreasFeatureByFarm } from './samplingAreas/saveSamplingAreas'

type FeatureProperties = {
  name: string
  otherName?: string
  samplingAreaId?: string
  isRandomSite?: boolean
}

type PolygonFeature = {
  properties: FeatureProperties
  geometry: Geometry
}

type PointFeature = {
  properties: FeatureProperties
  geometry: { type: 'Point'; coordinates: readonly [number, number] }
}

export type SaveFarmMappingLayer = 'monitoringSites' | 'otherPolygons' | 'otherSites'

export type SaveFarmMappingFailure = {
  layer: SaveFarmMappingLayer
  name: string
  error: Error
}

export type SaveFarmMappingInput = {
  farm: { id: string; name: string }
  selectedYears: (string | number)[]
  center: [number, number]
  perimeter?: PolygonFeature | null
  paddocks?: PolygonFeature[] | null
  samplingAreas?: ToGeoJSON[] | null
  monitoringSites?: PointFeature[] | null
  exclusionAreas?: PolygonFeature[] | null
  exclusionAreaTypes?: { id: number; es_AR: string }[] | null
  otherPolygons?: PolygonFeature[] | null
  otherSites?: PointFeature[] | null
}

const toMonitoringSiteEntity = ({ farmId, feature }: { farmId: string; feature: PointFeature }) => {
  const [lng, lat] = feature.geometry.coordinates
  return {
    farmId,
    name: feature.properties.name,
    samplingAreaId: feature.properties.samplingAreaId ?? null,
    // The MonitoringSite model stores plannedLocation as [lat, lng], the reverse of GeoJSON.
    plannedLocation: [lat, lng],
    isRandomSite: feature.properties.isRandomSite ?? false,
  } as MonitoringSite
}

// The id on a KML draft is the array index the importer stamps, not a database id: clearing it
// makes the POST create new rows instead of upserting whatever UUID it collides with.
const withoutImportedId = <T extends { properties: Record<string, unknown> }>(feature: T) => ({
  ...feature,
  properties: { ...feature.properties, id: undefined },
})

// The steps up to the exclusion areas are chained because each one feeds the next (the strata ids
// land on the sites, the paddock ids on the subdivision), so a failure there invalidates the rest.
// The per-feature layers below are independent: one rejected feature is reported by name in
// `failures` instead of aborting the whole save.
export const saveFarmMapping = async ({
  farm,
  selectedYears,
  center,
  perimeter,
  paddocks,
  samplingAreas,
  monitoringSites,
  exclusionAreas,
  exclusionAreaTypes,
  otherPolygons,
  otherSites,
}: SaveFarmMappingInput): Promise<{ failures: SaveFarmMappingFailure[] }> => {
  if (perimeter) {
    await updatePerimeter({ farm, geometry: perimeter.geometry, center })
  }

  const years = (selectedYears ?? []).map(year => parseInt(String(year), 10))
  if (years.length && paddocks?.length) {
    await Promise.all(
      years.map(async year => {
        const paddockIds = await paddocksService.savePaddocksFromKML({ farm, paddocks })
        await saveFarmSubdivision({ farm, data: { year, paddockIds } })
      }),
    )
  }

  if (samplingAreas?.length) {
    const createdSamplingAreas = await createSamplingAreasFeatureByFarm({
      farmId: farm.id,
      features: samplingAreas.map(withoutImportedId),
    })
    // Stamps samplingAreaId in place on the site features that fall inside a stratum.
    assignSamplingAreasToMonitoringPoints(
      createdSamplingAreas.map(samplingArea => samplingArea.toGeoJSON),
      monitoringSites,
    )
  }

  if (exclusionAreas?.length) {
    await createExclusionAreasFeatureByFarm({
      farmId: farm.id,
      features: exclusionAreas.map(feature =>
        withoutImportedId({
          ...feature,
          properties: {
            ...feature.properties,
            otherName: feature.properties.otherName ?? feature.properties.name,
          },
        }),
      ),
      exclusionTypes: exclusionAreaTypes,
    })
  }

  const featureWrites = [
    ...(monitoringSites ?? []).map(feature => ({
      layer: 'monitoringSites' as const,
      name: feature.properties.name,
      write: () =>
        addMonitoringSiteV2({ site: toMonitoringSiteEntity({ farmId: farm.id, feature }) }),
    })),
    ...(otherPolygons ?? []).map(feature => ({
      layer: 'otherPolygons' as const,
      name: feature.properties.name,
      write: () => createOtherPolygon({ farmId: farm.id, feature }),
    })),
    ...(otherSites ?? []).map(feature => ({
      layer: 'otherSites' as const,
      name: feature.properties.name,
      write: () => createOtherSite({ farmId: farm.id, feature }),
    })),
  ]

  const results = await Promise.allSettled(featureWrites.map(({ write }) => write()))

  const failures = results.flatMap((result, index) =>
    result.status === 'rejected'
      ? [
          {
            layer: featureWrites[index].layer,
            name: featureWrites[index].name,
            error: result.reason,
          },
        ]
      : [],
  )

  return { failures }
}
